import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/CheckApi';
import { moneyToText } from '../../api/utils';
import './Reports.css';

/**
 * SMS Grup | Çek Raporları
 * Mevcut CheckApi metodlarını kullanır; ek UI kütüphanesi gerektirmez.
 * NOT: JWT içindeki rol yalnızca arayüz görünürlüğünü belirler.
 * Gerçek erişim/yetki kontrolü backend tarafında uygulanmalıdır.
 */

const PAGE_SIZE = 10;
const CURRENCY_COLORS = ['#2563eb', '#0d9488', '#8b5cf6', '#e59a3e', '#ef6475', '#64748b'];
const MONEY_FORMATTER = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const NUMBER_FORMATTER = new Intl.NumberFormat('tr-TR');

const Icon = ({ name, size = 19, strokeWidth = 1.8, className = '' }) => {
  const paths = {
    chart: <><path d="M3 3v18h18"/><path d="M7 15v-4M12 15V7M17 15V5"/></>,
    download: <><path d="M12 3v12m0 0 5-5m-5 5-5-5"/><path d="M5 17v3h14v-3"/></>,
    refresh: <><path d="M20 7v5h-5M4 17v-5h5"/><path d="M5.8 9A7 7 0 0 1 18 7l2 5M4 12l2 5a7 7 0 0 0 12.2-2"/></>,
    files: <><rect x="8" y="4" width="12" height="16" rx="2"/><path d="M4 16V6a2 2 0 0 1 2-2h2M11 9h6M11 13h6"/></>,
    clock: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
    alert: <><path d="m12 3 9 16H3L12 3Z"/><path d="M12 9v4m0 3h.01"/></>,
    building: <><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 7h2m4 0h2M8 11h2m4 0h2M8 15h2m4 0h2M10 21v-4h4v4"/></>,
    search: <><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/></>,
    filter: <><path d="M4 7h16M7 12h10M10 17h4"/></>,
    close: <path d="M5 5l14 14M19 5 5 19"/>,
    chevronLeft: <path d="m15 18-6-6 6-6"/>,
    chevronRight: <path d="m9 18 6-6-6-6"/>,
    eye: <><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></>,
    edit: <><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L9 17l-4 1 1-4 10.5-10.5Z"/></>,
    trash: <><path d="M4 7h16M10 3h4m-8 4 1 14h10l1-14M10 11v6m4-6v6"/></>,
    check: <><circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/></>,
    info: <><circle cx="12" cy="12" r="9"/><path d="M12 11v5m0-8h.01"/></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M7 3v4m10-4v4M3 10h18"/></>,
    arrowUp: <path d="m5 15 6-6 4 4 5-7m-5 0h5v5"/>,
    wallet: <><rect x="3" y="5" width="18" height="16" rx="3"/><path d="M3 10h18M17 15h1"/></>,
    menu: <path d="M4 7h16M4 12h16M4 17h16"/>,
    banknote: <><rect x="2" y="5" width="20" height="14" rx="2"/><circle cx="12" cy="12" r="3"/><path d="M6 9h.01M18 15h.01"/></>,
    print: <><path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><path d="M6 15h12v6H6z"/></>,
  };
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width={size} height={size}
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name] || paths.info}
    </svg>
  );
};

function getRoleFromToken() {
  try {
    const token = localStorage.getItem('token');
    const part = token?.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return JSON.parse(atob(padded)).role || null;
  } catch (_error) {
    return null;
  }
}

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  let y, m, d;
  let match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/);
  if (match) [, y, m, d] = match;
  if (!match) {
    match = s.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})(?:[\s].*)?$/);
    if (match) [, d, m, y] = match;
  }
  if (match) {
    const result = new Date(Number(y), Number(m) - 1, Number(d));
    return result.getFullYear() === Number(y) &&
      result.getMonth() === Number(m) - 1 && result.getDate() === Number(d)
      ? result : null;
  }
  const fallback = new Date(s);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function localDayStamp(date) {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

function daysUntil(value, today) {
  const parsed = parseDate(value);
  return parsed ? Math.round((localDayStamp(parsed) - localDayStamp(today)) / 86400000) : null;
}

function getDueState(value, today) {
  const days = daysUntil(value, today);
  if (days === null) return { key: 'unknown', label: 'Tarih belirtilmemiş', days };
  if (days < 0) return { key: 'overdue', label: 'Vadesi geçmiş', days };
  if (days === 0) return { key: 'today', label: 'Bugün vadeli', days };
  if (days <= 30) return { key: 'soon', label: `${days} gün kaldı`, days };
  return { key: 'future', label: 'İleri tarihli', days };
}

function formatDate(value) {
  const parsed = parseDate(value);
  return parsed ? parsed.toLocaleDateString('tr-TR') : '—';
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
  }
  return String(value);
}

function toDateInput(value) {
  const d = parseDate(value);
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function toAmount(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const str = String(value ?? '').replace(/\s/g, '');
  if (!str) return 0;
  // Türkçe biçim (1.234,56) veya JSON sayısı (1234.56).
  const normalized = str.includes(',') ? str.replace(/\./g, '').replace(',', '.') : str;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
}

function currencyOf(item) {
  return String(item.currency || 'TL').trim().toUpperCase();
}

function cleanSearch(str) {
  return String(str ?? '').toLocaleLowerCase('tr-TR').trim();
}

function MetricCard({ label, value, caption, icon, tone, onClick, active }) {
  const body = (
    <>
      <span className={`cr-metric-icon cr-tone-${tone}`}><Icon name={icon} size={20}/></span>
      <span className="cr-metric-text">
        <span className="cr-metric-label">{label}</span>
        <span className="cr-metric-value">{value}</span>
        <span className="cr-metric-caption">{caption}</span>
      </span>
      {onClick && <span className="cr-metric-arrow"><Icon name="chevronRight" size={16}/></span>}
    </>
  );
  return onClick ? (
    <button type="button" className={`cr-metric cr-metric-clickable ${active ? 'cr-metric-active' : ''}`}
      onClick={onClick} aria-pressed={active}>{body}</button>
  ) : <div className="cr-metric">{body}</div>;
}

function Modal({ title, eyebrow, onClose, children, size = 'medium' }) {
  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="cr-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className={`cr-dialog cr-dialog-${size}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="cr-dialog-head">
          <div>{eyebrow && <div className="cr-eyebrow">{eyebrow}</div>}<h3>{title}</h3></div>
          <button className="cr-icon-button" type="button" onClick={onClose} aria-label="Kapat"><Icon name="close"/></button>
        </div>
        {children}
      </section>
    </div>
  );
}

const Reports = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [query, setQuery] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const [detailRecord, setDetailRecord] = useState(null);
  const [editData, setEditData] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Component bir kez yüklenince rolü okur. Backend yetkilendirmesinin yerine geçmez.
  const [isAdmin] = useState(() => getRoleFromToken() === 'admin');
  const today = new Date();

  const loadHistory = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    setError('');
    try {
      const data = await api.getCheckHistory();
      if (!Array.isArray(data)) throw new Error('Beklenmeyen veri biçimi.');
      setHistory(data);
      setLastUpdated(new Date());
      return true;
    } catch (err) {
      console.error('Çek geçmişi alınamadı:', err);
      setError('Kayıtlar yüklenemedi. Bağlantınızı kontrol edip yeniden deneyin.');
      return false;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadHistory(true); }, [loadHistory]);

  const stats = useMemo(() => {
    const currencies = {};
    const currencyCounts = {};
    const firmMap = new Map();
    const statusCounts = { overdue: 0, today: 0, soon: 0, future: 0, unknown: 0 };
    for (const item of history) {
      const curr = currencyOf(item);
      currencies[curr] = (currencies[curr] || 0) + toAmount(item.amount);
      currencyCounts[curr] = (currencyCounts[curr] || 0) + 1;
      const company = String(item.company_name || 'Firma belirtilmemiş').trim();
      const key = cleanSearch(company);
      if (!firmMap.has(key)) firmMap.set(key, { name: company, count: 0 });
      firmMap.get(key).count += 1;
      statusCounts[getDueState(item.check_date, today).key] += 1;
    }
    const firms = Array.from(firmMap.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'tr'));
    return { currencies, currencyCounts, firms, statusCounts, firmCount: firmMap.size };
  // Vade durumları sayfa açıldığındaki gün baz alınarak hesaplanır.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, new Date().toDateString()]);

  const currencyKeys = useMemo(() => Object.keys(stats.currencyCounts).sort((a, b) => (
    stats.currencyCounts[b] - stats.currencyCounts[a]
  )), [stats.currencyCounts]);

  const filteredRows = useMemo(() => {
    const search = cleanSearch(query);
    const filtered = history.filter((item) => {
      if (currencyFilter !== 'all' && currencyOf(item) !== currencyFilter) return false;
      const due = getDueState(item.check_date, today);
      if (statusFilter === 'within30' && !['soon', 'today'].includes(due.key)) return false;
      if (statusFilter !== 'all' && statusFilter !== 'within30' && due.key !== statusFilter) return false;
      if (search && ![
        item.id, item.company_name, item.company_tax_no,
        item.check_location, item.amount_text, item.currency,
      ].some((value) => cleanSearch(value).includes(search))) return false;
      const date = parseDate(item.check_date);
      if (dateFrom && (!date || toDateInput(date) < dateFrom)) return false;
      if (dateTo && (!date || toDateInput(date) > dateTo)) return false;
      return true;
    });
    filtered.sort((a, b) => {
      if (sortOrder === 'oldest') return Number(a.id) - Number(b.id);
      if (sortOrder === 'amount_desc') return toAmount(b.amount) - toAmount(a.amount);
      if (sortOrder === 'amount_asc') return toAmount(a.amount) - toAmount(b.amount);
      if (sortOrder === 'due_asc' || sortOrder === 'due_desc') {
        const aDate = parseDate(a.check_date);
        const bDate = parseDate(b.check_date);
        if (!aDate) return bDate ? 1 : 0;
        if (!bDate) return -1;
        return (sortOrder === 'due_asc' ? 1 : -1) * (localDayStamp(aDate) - localDayStamp(bDate));
      }
      return Number(b.id) - Number(a.id);
    });
    return filtered;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, query, currencyFilter, statusFilter, sortOrder, dateFrom, dateTo, new Date().toDateString()]);

  useEffect(() => { setPage(1); }, [query, currencyFilter, statusFilter, sortOrder, dateFrom, dateTo]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const effectivePage = Math.min(page, pageCount);
  const visibleRows = filteredRows.slice((effectivePage - 1) * PAGE_SIZE, effectivePage * PAGE_SIZE);
  const filtered = Boolean(query || currencyFilter !== 'all' || statusFilter !== 'all' || dateFrom || dateTo);

  const monthlyData = useMemo(() => {
    const current = new Date();
    const months = Array.from({ length: 6 }, (_, index) => {
      const d = new Date(current.getFullYear(), current.getMonth() - 2 + index, 1);
      return {
        key: `${d.getFullYear()}-${d.getMonth()}`,
        label: d.toLocaleDateString('tr-TR', { month: 'short' }),
        year: d.getFullYear(),
        month: d.getMonth(),
        count: 0,
      };
    });
    for (const item of history) {
      const d = parseDate(item.check_date);
      const month = months.find((m) => d && m.year === d.getFullYear() && m.month === d.getMonth());
      if (month) month.count += 1;
    }
    return months;
  }, [history]);

  const maxMonth = Math.max(1, ...monthlyData.map((m) => m.count));
  const maxFirm = Math.max(1, ...stats.firms.slice(0, 5).map((f) => f.count));
  const donutGradient = useMemo(() => {
    const count = history.length;
    if (!count) return 'conic-gradient(#e2e8f0 0% 100%)';
    let accumulated = 0;
    const stops = currencyKeys.map((curr, i) => {
      const start = accumulated;
      accumulated += (stats.currencyCounts[curr] / count) * 100;
      return `${CURRENCY_COLORS[i % CURRENCY_COLORS.length]} ${start}% ${accumulated}%`;
    });
    return `conic-gradient(${stops.join(', ')})`;
  }, [currencyKeys, history.length, stats.currencyCounts]);

  const resetFilters = () => {
    setQuery(''); setCurrencyFilter('all'); setStatusFilter('all');
    setDateFrom(''); setDateTo(''); setSortOrder('newest'); setPage(1);
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // Mevcut API'yi korur; sunucudaki kurumsal tasarımlı tam raporu indirir.
      const response = await api.exportExcel();
      const blob = response instanceof Blob ? response : response?.data;
      if (!(blob instanceof Blob) || !blob.size) throw new Error('Excel dosyası alınamadı.');
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = `SMS_Grup_Cek_Raporu_${toDateInput(new Date())}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      setNotice({ type: 'success', message: 'Excel raporu indirme işlemi başlatıldı.' });
    } catch (err) {
      console.error('Excel indirme hatası:', err);
      setNotice({ type: 'error', message: 'Excel raporu indirilemedi. API bağlantısını kontrol edin.' });
    } finally {
      setExporting(false);
    }
  };

  const beginEdit = (item) => {
    setEditData({ ...item, amount: String(item.amount ?? ''), currency: currencyOf(item), check_date: toDateInput(item.check_date) });
    setDetailRecord(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, [name]: value };
      if (name === 'amount' || name === 'currency') {
        try { updated.amount_text = moneyToText(updated.amount, updated.currency); }
        catch (_error) { updated.amount_text = ''; }
      }
      return updated;
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editData || saving) return;
    const amount = toAmount(editData.amount);
    if (!editData.company_name?.trim() || !editData.check_date || !Number.isFinite(amount) || amount < 0 || String(editData.amount).trim() === '') {
      setNotice({ type: 'error', message: 'Firma, geçerli tutar ve vade tarihi alanlarını kontrol edin.' });
      return;
    }
    setSaving(true);
    try {
      await api.updateCheckHistory(editData.id, { ...editData, amount });
      setEditData(null);
      setNotice({ type: 'success', message: `#${editData.id} numaralı çek kaydı güncellendi.` });
      await loadHistory();
    } catch (err) {
      console.error('Çek güncellenemedi:', err);
      setNotice({ type: 'error', message: 'Kayıt güncellenemedi. Değişiklikler kaydedilmedi.' });
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteRecord || deleting) return;
    setDeleting(true);
    try {
      const id = deleteRecord.id;
      await api.deleteCheckHistory(id);
      setDeleteRecord(null);
      setHistory((prev) => prev.filter((item) => item.id !== id));
      setNotice({ type: 'success', message: `#${id} numaralı çek kaydı silindi.` });
      await loadHistory();
    } catch (err) {
      console.error('Çek silinemedi:', err);
      setNotice({ type: 'error', message: 'Çek kaydı silinemedi. Yetki ve bağlantıyı kontrol edin.' });
    } finally { setDeleting(false); }
  };

  return (
    <div className="cr-page">
      <div className="cr-container">
        <div className="cr-breadcrumb"><span>Finans Yönetimi</span><Icon name="chevronRight" size={13}/><strong>Çek Raporları</strong></div>

        <section className="cr-hero">
          <div className="cr-hero-orb cr-orb-one"/><div className="cr-hero-orb cr-orb-two"/>
          <div className="cr-hero-content">
            <div className="cr-hero-overline"><span className="cr-live-dot"/> FİNANSAL RAPORLAMA MERKEZİ</div>
            <h1>Çek Raporları <span>&amp; Analiz</span></h1>
            <p>Çek portföyünüzü tek ekrandan inceleyin, vade hareketlerini takip edin ve kapsamlı Excel raporunuzu oluşturun.</p>
            <div className="cr-hero-meta">
              <span><Icon name="calendar" size={15}/>{new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              <span><Icon name="check" size={15}/>{lastUpdated ? `Son güncelleme ${lastUpdated.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}` : 'Canlı kayıt görünümü'}</span>
            </div>
          </div>
          <div className="cr-hero-actions">
            <button className="cr-button cr-button-hero-ghost" type="button" disabled={refreshing || loading} onClick={() => loadHistory()}>
              <Icon name="refresh" size={17} className={refreshing ? 'cr-spin' : ''}/>
              {refreshing ? 'Yenileniyor' : 'Yenile'}
            </button>
            <button className="cr-button cr-button-export" type="button" disabled={exporting || loading} onClick={handleExport}>
              <Icon name="download" size={19}/>{exporting ? 'Hazırlanıyor...' : 'Excel Raporu İndir'}
            </button>
            <small>Excel çıktısı tüm kayıtları içerir.</small>
          </div>
        </section>

        {error && <div className="cr-alert cr-alert-error" role="alert"><Icon name="alert"/><span>{error}</span><button type="button" onClick={() => loadHistory()}>Tekrar Dene</button></div>}
        {notice && <div className={`cr-alert cr-alert-${notice.type}`} role="status"><Icon name={notice.type === 'success' ? 'check' : 'alert'}/><span>{notice.message}</span><button className="cr-alert-close" type="button" onClick={() => setNotice(null)} aria-label="Mesajı kapat"><Icon name="close" size={16}/></button></div>}

        <div className="cr-section-label"><div><span className="cr-section-rail"/> Genel görünüm</div><span>Portföy özeti</span></div>
        <section className="cr-metrics" aria-label="Özet göstergeler">
          <MetricCard label="Toplam Çek Kaydı" value={NUMBER_FORMATTER.format(history.length)} icon="files" tone="blue"
            caption="Kayıtlı tüm çekler" active={statusFilter === 'all' && !filtered} onClick={() => { resetFilters(); }}/>
          <MetricCard label="Vadesi Geçmiş" value={NUMBER_FORMATTER.format(stats.statusCounts.overdue)} icon="alert" tone="red"
            caption="Vade tarihi geçmiş kayıtlar" active={statusFilter === 'overdue'} onClick={() => setStatusFilter('overdue')}/>
          <MetricCard label="30 Gün İçinde" value={NUMBER_FORMATTER.format(stats.statusCounts.soon + stats.statusCounts.today)} icon="clock" tone="amber"
            caption="Bugün ve gelecek 30 gün" active={statusFilter === 'within30'} onClick={() => setStatusFilter('within30')}/>
          <MetricCard label="Toplam Firma" value={NUMBER_FORMATTER.format(stats.firmCount)} icon="building" tone="teal"
            caption="Farklı firma kaydı"/>
        </section>
        <p className="cr-metric-note"><Icon name="info" size={14}/> Vade uyarıları yalnızca çek tarihine göre hesaplanır; tahsilat veya ödeme durumunu ifade etmez.</p>

        <section className="cr-analytics-grid">
          <article className="cr-panel cr-analytics-main">
            <div className="cr-panel-header">
              <div><div className="cr-panel-kicker">VADE DAĞILIMI</div><h2>Aylık Çek Hareketi</h2><p>Geçmiş 2 ay, mevcut ay ve gelecek 3 ay</p></div>
              <span className="cr-panel-symbol"><Icon name="chart" size={19}/></span>
            </div>
            <div className="cr-month-chart" role="img" aria-label="Vade aylarına göre çek adedi grafiği">
              {monthlyData.map((m) => (
                <div className="cr-month-column" key={m.key} title={`${m.label} ${m.year}: ${m.count} çek`}>
                  <span className="cr-month-count">{m.count}</span>
                  <div className="cr-month-track"><div className={`cr-month-fill ${m.month === new Date().getMonth() && m.year === new Date().getFullYear() ? 'cr-month-current' : ''}`}
                    style={{ height: `${m.count ? Math.max(10, 100 * m.count / maxMonth) : 0}%` }}/></div>
                  <span className="cr-month-label">{m.label}</span>
                </div>
              ))}
            </div>
            <div className="cr-chart-footer"><span className="cr-chart-dot"/> Grafik çek tutarını değil, vade ayına düşen kayıt adedini gösterir.</div>
          </article>

          <article className="cr-panel cr-distribution-panel">
            <div className="cr-panel-header">
              <div><div className="cr-panel-kicker">PORTFÖY DAĞILIMI</div><h2>Para Birimi Analizi</h2><p>Para birimine göre çek adedi</p></div>
              <span className="cr-panel-symbol"><Icon name="wallet" size={19}/></span>
            </div>
            <div className="cr-distribution-body">
              <div className="cr-donut" style={{ background: donutGradient }} role="img" aria-label="Para birimlerine göre çek adedi dağılımı">
                <div className="cr-donut-inner"><strong>{NUMBER_FORMATTER.format(history.length)}</strong><span>TOPLAM ÇEK</span></div>
              </div>
              <div className="cr-donut-legend">
                {currencyKeys.length ? currencyKeys.map((curr, i) => (
                  <div className="cr-legend-line" key={curr}>
                    <span className="cr-legend-name"><i style={{ background: CURRENCY_COLORS[i % CURRENCY_COLORS.length] }}/>{curr}</span>
                    <span><strong>{stats.currencyCounts[curr]}</strong><small>%{Math.round(100 * stats.currencyCounts[curr] / history.length)}</small></span>
                  </div>
                )) : <p className="cr-subtle">Henüz çek kaydı yok.</p>}
              </div>
            </div>
          </article>
        </section>

        <section className={`cr-bottom-analytics ${isAdmin ? '' : 'cr-bottom-single'}`}>
          <article className="cr-panel cr-firm-panel">
            <div className="cr-panel-header">
              <div><div className="cr-panel-kicker">FİRMA ANALİZİ</div><h2>En Çok Çek Kaydı Olan Firmalar</h2><p>Kayıt sayısına göre ilk 5 firma</p></div>
              <span className="cr-panel-symbol"><Icon name="building" size={19}/></span>
            </div>
            {stats.firms.length ? (
              <div className="cr-firm-list">
                {stats.firms.slice(0, 5).map((firm, index) => (
                  <div className="cr-firm-item" key={`${firm.name}-${index}`}>
                    <div className="cr-firm-row"><span><i className="cr-rank">{String(index + 1).padStart(2, '0')}</i><span className="cr-firm-name" title={firm.name}>{firm.name}</span></span><strong>{firm.count} çek</strong></div>
                    <div className="cr-progress"><span style={{ width: `${100 * firm.count / maxFirm}%` }}/></div>
                  </div>
                ))}
              </div>
            ) : <div className="cr-mini-empty">Firma verisi bulunmuyor.</div>}
          </article>

          {isAdmin && (
            <article className="cr-panel cr-money-panel">
              <div className="cr-panel-header"><div><div className="cr-panel-kicker">YÖNETİCİ ÖZETİ</div><h2>Para Birimine Göre Toplamlar</h2><p>Kur dönüşümü yapılmadan ayrı ayrı gösterilir</p></div><span className="cr-panel-symbol"><Icon name="banknote" size={19}/></span></div>
              <div className="cr-money-list">
                {Object.entries(stats.currencies).sort(([a], [b]) => a.localeCompare(b)).length ?
                  Object.entries(stats.currencies).sort(([a], [b]) => a.localeCompare(b)).map(([curr, amount]) => (
                    <div className="cr-money-row" key={curr}><span className="cr-currency-tag">{curr}</span><strong>{MONEY_FORMATTER.format(amount)} <em>{curr}</em></strong></div>
                  )) : <div className="cr-mini-empty">Tutar bilgisi bulunmuyor.</div>}
              </div>
              <div className="cr-money-foot"><Icon name="info" size={15}/> Yalnızca admin arayüzünde gösterilir.</div>
            </article>
          )}
        </section>

        <section className="cr-panel cr-table-panel" id="cek-kayitlari">
          <div className="cr-table-heading">
            <div><div className="cr-panel-kicker">KAYIT YÖNETİMİ</div><h2>Çek Kayıtları</h2><p>Arayın, filtreleyin, detay görüntüleyin veya düzenleyin.</p></div>
            <span className="cr-result-pill"><span className="cr-live-dot"/>{filteredRows.length} / {history.length} kayıt</span>
          </div>
          <div className="cr-toolbar">
            <label className="cr-search"><Icon name="search" size={19}/><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Firma, vergi no, çek no veya konum ara..." aria-label="Çek kayıtlarında ara"/>{query && <button type="button" onClick={() => setQuery('')} aria-label="Aramayı temizle"><Icon name="close" size={15}/></button>}</label>
            <div className="cr-select-wrap"><Icon name="filter" size={17}/><select aria-label="Vade durumuna göre filtrele" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Tüm vadeler</option><option value="overdue">Vadesi geçmiş</option><option value="today">Bugün vadeli</option><option value="within30">Bugün ve gelecek 30 gün</option><option value="soon">1–30 gün sonra</option><option value="future">İleri tarihli</option><option value="unknown">Tarih belirtilmemiş</option>
            </select></div>
            <div className="cr-select-wrap"><select value={currencyFilter} onChange={(e) => setCurrencyFilter(e.target.value)} aria-label="Para birimine göre filtrele"><option value="all">Tüm para birimleri</option>{currencyKeys.map((curr) => <option key={curr} value={curr}>{curr}</option>)}</select></div>
            <div className="cr-select-wrap"><select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} aria-label="Kayıtları sırala"><option value="newest">Yeni kayıtlar</option><option value="oldest">Eski kayıtlar</option><option value="due_asc">En yakın vade</option><option value="due_desc">En uzak vade</option><option value="amount_desc">Tutar: yüksekten</option><option value="amount_asc">Tutar: düşükten</option></select></div>
          </div>
          <div className="cr-date-filters"><span><Icon name="calendar" size={16}/> Vade aralığı</span><label>Başlangıç <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)}/></label><label>Bitiş <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)}/></label>{filtered && <button className="cr-reset" type="button" onClick={resetFilters}><Icon name="close" size={14}/> Filtreleri Temizle</button>}</div>

          {loading ? (
            <div className="cr-loading" role="status"><span className="cr-spinner"/> Çek kayıtları yükleniyor...</div>
          ) : filteredRows.length === 0 ? (
            <div className="cr-empty"><span className="cr-empty-symbol"><Icon name={history.length ? 'search' : 'files'} size={27}/></span><h3>{history.length ? 'Eşleşen çek bulunamadı' : 'Henüz çek kaydı bulunmuyor'}</h3><p>{history.length ? 'Arama veya filtre koşullarını değiştirebilirsiniz.' : 'Çek kayıtları oluşturulduğunda bu alanda listelenecek.'}</p>{filtered && <button className="cr-button cr-button-primary" type="button" onClick={resetFilters}>Filtreleri sıfırla</button>}</div>
          ) : (
            <>
              <div className="cr-table-scroll">
                <table className="cr-table">
                  <thead><tr><th>ÇEK NO</th><th>FİRMA BİLGİLERİ</th><th>TUTAR</th><th>VADE TARİHİ</th><th>VADE DURUMU</th><th>KAYIT TARİHİ</th><th className="cr-right">İŞLEMLER</th></tr></thead>
                  <tbody>{visibleRows.map((item) => {
                    const due = getDueState(item.check_date, today);
                    return <tr key={item.id}>
                      <td><span className="cr-row-id">#{item.id}</span></td>
                      <td><div className="cr-company-cell"><span className="cr-company-avatar">{String(item.company_name || '?').slice(0, 1).toLocaleUpperCase('tr-TR')}</span><span><strong title={item.company_name || ''}>{item.company_name || 'Firma belirtilmemiş'}</strong><small>VKN: {item.company_tax_no || '—'}</small></span></div></td>
                      <td><div className="cr-table-amount">{MONEY_FORMATTER.format(toAmount(item.amount))}<small>{currencyOf(item)}</small></div></td>
                      <td><span className="cr-cell-date"><Icon name="calendar" size={15}/>{formatDate(item.check_date)}</span></td>
                      <td><span className={`cr-status cr-status-${due.key}`}><i/>{due.label}</span></td>
                      <td><span className="cr-registration-date">{formatDateTime(item.created_at)}</span></td>
                      <td><div className="cr-row-actions"><button type="button" title="Detayları görüntüle" aria-label={`#${item.id} detaylarını görüntüle`} onClick={() => setDetailRecord(item)}><Icon name="eye" size={17}/></button><button type="button" title="Kaydı düzenle" aria-label={`#${item.id} kaydını düzenle`} onClick={() => beginEdit(item)}><Icon name="edit" size={17}/></button><button className="cr-row-danger" type="button" title="Kaydı sil" aria-label={`#${item.id} kaydını sil`} onClick={() => setDeleteRecord(item)}><Icon name="trash" size={17}/></button></div></td>
                    </tr>;
                  })}</tbody>
                </table>
              </div>
              <div className="cr-pagination"><span>{(effectivePage - 1) * PAGE_SIZE + 1}–{Math.min(effectivePage * PAGE_SIZE, filteredRows.length)} / {filteredRows.length} kayıt gösteriliyor</span><div><button type="button" disabled={effectivePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}><Icon name="chevronLeft" size={17}/> Önceki</button><span className="cr-page-indicator">{effectivePage} / {pageCount}</span><button type="button" disabled={effectivePage >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>Sonraki <Icon name="chevronRight" size={17}/></button></div></div>
            </>
          )}
        </section>
        <footer className="cr-footer"><span>SMS GRUP · Çek Yönetimi / Raporlama</span><span>Gösterilen veriler, çek kayıtlarına dayalıdır.</span></footer>
      </div>

      {detailRecord && <Modal title={`Çek #${detailRecord.id}`} eyebrow="ÇEK KAYDI DETAYLARI" onClose={() => setDetailRecord(null)}>
        <div className="cr-detail-highlight"><span>Çek Tutarı</span><strong>{MONEY_FORMATTER.format(toAmount(detailRecord.amount))} {currencyOf(detailRecord)}</strong><span className={`cr-status cr-status-${getDueState(detailRecord.check_date, today).key}`}>{getDueState(detailRecord.check_date, today).label}</span></div>
        <div className="cr-detail-grid">
          <div><span>Firma</span><strong>{detailRecord.company_name || '—'}</strong></div>
          <div><span>Vergi Numarası</span><strong>{detailRecord.company_tax_no || '—'}</strong></div>
          <div><span>Çek Tarihi (Vade)</span><strong>{formatDate(detailRecord.check_date)}</strong></div>
          <div><span>Çek Yeri</span><strong>{detailRecord.check_location || '—'}</strong></div>
          <div><span>Kayıt Tarihi</span><strong>{formatDateTime(detailRecord.created_at)}</strong></div>
          <div><span>Para Birimi</span><strong>{currencyOf(detailRecord)}</strong></div>
          <div className="cr-detail-full"><span>Yazıyla Tutar</span><strong>{detailRecord.amount_text || '—'}</strong></div>
        </div>
        <div className="cr-dialog-footer"><button className="cr-button cr-button-plain" type="button" onClick={() => setDetailRecord(null)}>Kapat</button><button className="cr-button cr-button-primary" type="button" onClick={() => beginEdit(detailRecord)}><Icon name="edit" size={16}/> Düzenle</button></div>
      </Modal>}

      {editData && <Modal title={`Çek #${editData.id} · Düzenle`} eyebrow="KAYIT GÜNCELLEME" onClose={() => { if (!saving) setEditData(null); }} size="large">
        <form onSubmit={saveEdit}>
          <div className="cr-form-grid">
            <label className="cr-form-full">Firma Ünvanı <input required name="company_name" value={editData.company_name ?? ''} onChange={handleEditChange} placeholder="Firma adı"/></label>
            <label>Vergi Numarası <input name="company_tax_no" value={editData.company_tax_no ?? ''} onChange={handleEditChange} placeholder="Vergi numarası"/></label>
            <label>Çek Yeri <input name="check_location" value={editData.check_location ?? ''} onChange={handleEditChange} placeholder="Düzenleme yeri"/></label>
            <label>Vade Tarihi <input required type="date" name="check_date" value={editData.check_date ?? ''} onChange={handleEditChange}/></label>
            <label>Para Birimi <select name="currency" value={editData.currency} onChange={handleEditChange}>{Array.from(new Set(['TL', 'USD', 'EUR', 'GBP', ...currencyKeys])).map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
            <label className="cr-form-full">Çek Tutarı <input required type="number" name="amount" min="0" step="0.01" value={editData.amount ?? ''} onChange={handleEditChange}/></label>
            <label className="cr-form-full">Yazıyla Tutar <textarea readOnly name="amount_text" rows={2} value={editData.amount_text ?? ''}/></label>
          </div>
          <div className="cr-dialog-footer"><button type="button" className="cr-button cr-button-plain" disabled={saving} onClick={() => setEditData(null)}>Vazgeç</button><button type="submit" disabled={saving} className="cr-button cr-button-primary"><Icon name="check" size={17}/>{saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}</button></div>
        </form>
      </Modal>}

      {deleteRecord && <Modal title="Çek kaydı silinsin mi?" eyebrow="SİLME ONAYI" onClose={() => { if (!deleting) setDeleteRecord(null); }} size="small">
        <div className="cr-delete-message"><span className="cr-delete-icon"><Icon name="alert" size={24}/></span><p><strong>#{deleteRecord.id}</strong> numaralı, <strong>{deleteRecord.company_name || 'firma bilgisi olmayan'}</strong> firmaya ait çek kaydı silinecek. Bu işlem geri alınamaz.</p></div>
        <div className="cr-dialog-footer"><button type="button" className="cr-button cr-button-plain" disabled={deleting} onClick={() => setDeleteRecord(null)}>İptal</button><button type="button" className="cr-button cr-button-danger" disabled={deleting} onClick={confirmDelete}><Icon name="trash" size={17}/>{deleting ? 'Siliniyor...' : 'Kaydı Sil'}</button></div>
      </Modal>}
    </div>
  );
};

export default Reports;
